import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * Lets a routed page make the globally-mounted Cammy dock say something of
 * its own — e.g. the Cases list summarising whichever row is under the
 * pointer. The dock is mounted in Layout, outside the route (see
 * PageSectionsContext for the same problem solved for in-page navigation),
 * so a page can't just call a method on it directly.
 *
 * Two sources can ask Cammy to talk: hovering a row (lasts exactly as long as
 * the hover) and clicking one (lasts a fixed duration regardless of what the
 * pointer does next — the row's Link opens in a new tab, so this list stays
 * mounted and the timer here runs to completion either way). A hover's
 * "stop talking" must not cut off an active click, so `clear` only clears a
 * message whose own source matches the one asking.
 *
 * A separate, independent concept lives in the same file: `pageSummary`, an
 * ambient fact about the current page (e.g. the case detail page's own case)
 * that the dock only surfaces while the pointer is over Parkhi himself —
 * see useCammyPageSummary/usePublishCammyPageSummary below.
 */

const CammySpeechContext = createContext({
  message: null,
  say: () => {},
  clear: () => {},
  pageSummary: null,
  publishPageSummary: () => {},
});

export function CammySpeechProvider({ children }) {
  const [message, setMessage] = useState(null); // { text, source }
  const timerRef = useRef(null);

  // ---- page summary — independent of message/say/clear above -------------
  // An ambient fact about whatever page is currently mounted (e.g. the case
  // detail page's own case), published once on mount the same way
  // PageSectionsContext's sections are, and left untouched by the
  // interaction-driven hover/click messages. The dock decides entirely on
  // its own when to actually surface it (only while the pointer is over
  // Parkhi himself) — this context just holds the latest value. Deleting
  // this block, its export below, and the one usePublishCammyPageSummary
  // call site in CaseDetailsRedesign.js leaves the hover/click row summaries
  // (and everything else in this file) completely unaffected.
  const [pageSummary, setPageSummary] = useState(null);
  const publishPageSummary = useCallback(
    (next) => setPageSummary(next || null),
    [],
  );

  const say = useCallback((text, { seconds, source = "hover", leanY } = {}) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setMessage((current) => {
      // A click's message runs for a fixed window — a hover started on some
      // other row in the meantime shouldn't be able to pre-empt it.
      if (current?.source === "click" && source === "hover") return current;
      return text ? { text, source, leanY } : null;
    });
    if (text && seconds) {
      timerRef.current = setTimeout(() => {
        setMessage(null);
        timerRef.current = null;
      }, seconds * 1000);
    }
  }, []);

  const clear = useCallback((source = "hover") => {
    setMessage((current) => {
      if (!current || current.source !== source) return current;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return null;
    });
  }, []);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const value = useMemo(
    () => ({ message, say, clear, pageSummary, publishPageSummary }),
    [message, say, clear, pageSummary, publishPageSummary],
  );
  return (
    <CammySpeechContext.Provider value={value}>
      {children}
    </CammySpeechContext.Provider>
  );
}

/** Read the current externally-requested message (used by the dock). */
export const useCammySpeechMessage = () => useContext(CammySpeechContext).message;

/**
 * Make Cammy say something.
 *
 *   say(text, { source: "hover" })               — lasts until cleared
 *   say(text, { source: "click", seconds: 5 })    — auto-clears itself
 *   say(text, { source: "hover", leanY })         — also glance up/down at
 *                                                    the viewport Y the text
 *                                                    is about (optional —
 *                                                    the dock ignores it when
 *                                                    absent)
 *   clear(source)                                  — only clears a same-source message
 */
export const useCammySay = () => {
  const { say, clear } = useContext(CammySpeechContext);
  return { say, clear };
};

/** Read the current page's published summary (used by the dock, on hover). */
export const useCammyPageSummary = () =>
  useContext(CammySpeechContext).pageSummary;

/**
 * Publish a page-level case/record summary for as long as the calling page
 * is mounted — mirrors usePublishPageSections exactly (publish on mount and
 * whenever it changes, clear on unmount). Pass a memoised object/null, not
 * one rebuilt every render, or this effect loops.
 */
export function usePublishCammyPageSummary(summary) {
  const { publishPageSummary } = useContext(CammySpeechContext);
  useEffect(() => {
    publishPageSummary(summary);
    return () => publishPageSummary(null);
  }, [summary, publishPageSummary]);
}
